import Component from '@glimmer/component'
import { service } from '@ember/service'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import { MathseedsPrimeThread } from 'teacher-ui-client/progression-sequence/utils'
import type Features from 'teacher-ui-client/services/features'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import { t } from 'ember-intl'
import { or } from 'ember-truth-helpers'
import { get } from '@ember/helper'

const { MS_LESSONS, MS_MENTAL_MINUTE } = Precinct
const { Number, Fractions, Operations, Geometry, Measurement } = MathseedsPrimeThread
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const rangeKeys = [MS_LESSONS, MS_MENTAL_MINUTE, Number, Fractions, Operations, Geometry, Measurement] as const

type LessonGuideRow = Partial<Record<'gradeName' | (typeof rangeKeys)[number], string>>

interface Signature {
  Element: HTMLTableElement
}

export class LessonGuideTableMaths extends Component<Signature> {
  @service gradeSets!: GradeSetsService

  @service featureControl!: FeatureControlService

  @service features!: Features

  get isMathseedsPrimeEnabled() {
    return this.featureControl.canAccess('msp_product')
  }

  get isMathseedsPrimeMeasurementEnabled() {
    return this.isMathseedsPrimeEnabled && this.features.isEnabled('tui_ms_prime_progress_measurement')
  }

  get isMathseedsPrimeGrade3Enabled() {
    return this.isMathseedsPrimeEnabled && this.features.isEnabled('tui_ms_prime_grade_three')
  }

  get isMathseedsPrimeGrade5Enabled() {
    return this.isMathseedsPrimeEnabled && this.features.isEnabled('tui_ms_prime_grade_five')
  }

  get isMathseedsPrimeGrade6Enabled() {
    return this.isMathseedsPrimeEnabled && this.features.isEnabled('tui_ms_prime_grade_six')
  }

  // calculated separately while feature flags are in play, to get an accurate colSpan number
  get mspRangeKeys() {
    if (!this.isMathseedsPrimeEnabled) return []
    if (!this.isMathseedsPrimeMeasurementEnabled) return [Number, Fractions, Operations, Geometry]
    return [Number, Fractions, Operations, Geometry, Measurement]
  }

  // when tui_ms_prime_progress_measurement enabled, just use rangeKeys const
  get rangeKeys() {
    return [MS_LESSONS, MS_MENTAL_MINUTE, ...this.mspRangeKeys] as const
  }

  // when tui_ms_prime_progress_measurement enabled, place directly in template
  get colWidthClass() {
    // add 1 for grades column
    switch (this.rangeKeys.length + 1) {
      case 3:
        return 'w-1/3'
      case 7:
        return 'w-[14.2%]' // 1/7
      case 8:
        return 'w-[12.5%]' // 1/8
      default:
        return ''
    }
  }

  get rows(): LessonGuideRow[] {
    const grade3MSPGuides = {
      [Number]: '1–3',
      [Fractions]: '1',
      [Operations]: '1–6',
      [Geometry]: '1–4',
      [Measurement]: '1–5',
    }

    const rows = [
      {
        gradeName: this.gradeSets.fullNameFromGradeSetByPosition(1),
        [MS_LESSONS]: '1–50',
        [MS_MENTAL_MINUTE]: '1–39',
      },
      {
        gradeName: this.gradeSets.fullNameFromGradeSetByPosition(2),
        [MS_LESSONS]: '51–100',
        [MS_MENTAL_MINUTE]: '40–71',
      },
      {
        gradeName: this.gradeSets.fullNameFromGradeSetByPosition(3),
        [MS_LESSONS]: '101–150',
        [MS_MENTAL_MINUTE]: '72–106',
      },
      {
        gradeName: this.gradeSets.fullNameFromGradeSetByPosition(4),
        [MS_LESSONS]: '151–200',
        [MS_MENTAL_MINUTE]: '107–145',
        ...(this.isMathseedsPrimeGrade3Enabled ? grade3MSPGuides : {}),
      },
      {
        gradeName: this.gradeSets.fullNameFromGradeSetByPosition(5),
        [Number]: '4–5',
        [Fractions]: '2–4',
        [Operations]: '7–14',
        [Geometry]: '5–8',
        [Measurement]: '6–12',
      },
    ]

    // note Grade 5 and Grade 6 (AU) is gradeposiiton 6 and 7
    const grade5Row = {
      gradeName: this.gradeSets.fullNameFromGradeSetByPosition(6),
      [Number]: '6–7',
      [Fractions]: '5–6',
      [Operations]: '15–25',
      [Geometry]: '9–13',
      [Measurement]: '13–19',
    }

    const grade6Row = {
      gradeName: this.gradeSets.fullNameFromGradeSetByPosition(7),
      [Number]: '8–9',
      [Fractions]: '7–11',
      [Operations]: '26–32',
      [Geometry]: '14–16',
      [Measurement]: '20–26',
    }

    return [
      ...rows,
      ...(this.isMathseedsPrimeGrade5Enabled ? [grade5Row] : []),
      ...(this.isMathseedsPrimeGrade6Enabled ? [grade6Row] : []),
    ]
  }

  <template>
    <table class="table-fixed" ...attributes>
      <thead>
        <tr>
          <th></th>
          <th class="table-box table-box-header bg-juicy-orange-200 text-center" colspan="2">
            {{t "tables.lessonGuide.headers.mathseeds.title"}}
          </th>
          {{#if this.isMathseedsPrimeEnabled}}
            <th class="table-box table-box-header bg-juicy-orange-200 text-center" colspan={{this.mspRangeKeys.length}}>
              {{t "tables.lessonGuide.headers.mathseedsPrime.title"}}
            </th>
          {{/if}}
        </tr>
        <tr class="h-20">
          <th></th>
          <th class="table-box table-box-header {{this.colWidthClass}} bg-juicy-orange-100 text-center">
            {{t "tables.lessonGuide.headers.mathseeds.lessons"}}
          </th>
          <th class="table-box table-box-header {{this.colWidthClass}} bg-juicy-orange-100 text-center">
            {{t "tables.lessonGuide.headers.mathseeds.mentalMinute"}}
          </th>
          {{#if this.isMathseedsPrimeEnabled}}
            <th class="table-box table-box-header {{this.colWidthClass}} bg-juicy-orange-100 text-center">
              {{t "tables.lessonGuide.headers.mathseedsPrime.number" htmlSafe=true}}
            </th>
            <th class="table-box table-box-header {{this.colWidthClass}} bg-juicy-orange-100 text-center">
              {{t "tables.lessonGuide.headers.mathseedsPrime.fractions" htmlSafe=true}}
            </th>
            <th class="table-box table-box-header {{this.colWidthClass}} bg-juicy-orange-100 text-center">
              {{t "tables.lessonGuide.headers.mathseedsPrime.operations" htmlSafe=true}}
            </th>
            <th class="table-box table-box-header {{this.colWidthClass}} bg-juicy-orange-100 text-center">
              {{t "tables.lessonGuide.headers.mathseedsPrime.geometry" htmlSafe=true}}
            </th>
            {{#if this.isMathseedsPrimeMeasurementEnabled}}
              <th class="table-box table-box-header {{this.colWidthClass}} bg-juicy-orange-100 text-center">
                {{t "tables.lessonGuide.headers.mathseedsPrime.measurement" htmlSafe=true}}
              </th>
            {{/if}}
          {{/if}}
        </tr>
      </thead>
      <tbody>
        {{#each this.rows as |row|}}
          <tr>
            <th class="table-box table-box-cell bg-juicy-orange-100 p-2 font-bold">{{row.gradeName}}</th>
            {{#each this.rangeKeys as |rangeKey|}}
              <td class="table-box table-box-cell bg-juicy-orange-50 p-2 text-center">
                {{or (get row rangeKey) "-"}}
              </td>
            {{/each}}
          </tr>
        {{/each}}
      </tbody>
    </table>
  </template>
}

export default LessonGuideTableMaths
